import axios from 'axios';
import axiosRetry from 'axios-retry';
import { history } from '../store';
import { path } from '../conf/route.conf';

class ApiService {
  constructor(conf) {
    this.lostId = null;
    this.xSessionId = null;

    this.maxRetry = conf.maxRetry || 3;
    this.retryDelay = conf.retryDelay || 300;

    this.axios = axios.create({
      baseURL: conf.host,
      timeout: conf.retryTimeout || 8000,
      headers: {
        'Accept': 'application/json; charset=utf8',
        'Accept-Language': 'fr, en',
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache',
      },
    });
    
    this.simpleAxios = axios.create({
      baseURL: conf.host,
      timeout: conf.retryTimeout || 8000,
      headers: {
        'Accept': 'application/json; charset=utf8',
        'Accept-Language': 'fr, en',
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache',
      },
    });

    this.setupInterceptors();
    this.setupRetry();
  }

  /**
   * Setup Retry
   */
  setupRetry() {
    axiosRetry(this.axios, {
      retries: this.maxRetry,
      retryDelay: () => this.retryDelay,
      shouldResetTimeout: true,
      retryCondition: this.getRetryCondition,
    });
  }

  /**
   * Setup request and response interceptors
   */
  setupInterceptors() {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Setup request interceptor
   */
  setupRequestInterceptor() {
    // Update headers in next axios request
    this.axios.interceptors.request.use((req) => {
      const reqOverride = req;

      if (this.xSessionId) {
        reqOverride.headers['X-Session-Id'] = this.xSessionId;
      }

      if (this.lostId) {
        reqOverride.headers['Lost-Id'] = this.lostId;
      }
      return reqOverride;
    });
  }

  /**
   * Setup response interceptor
   */
  setupResponseInterceptor() {
    // Update headers in next axios response
    this.axios.interceptors.response.use(
      (res) => {
        this.lostId = (res.headers && res.headers['lost-id']) || this.lostId;
        this.xSessionId = (res.headers && res.headers['x-session-id']) || this.xSessionId;

        return res;
      },
      (err) => {
        if (!err.response) {
          history.push(path.ERROR);
        }

        this.lostId = (err.response.headers && err.response.headers['lost-id']) || this.lostId;
        this.xSessionId = (err.response.headers && err.response.headers['x-session-id']) || this.xSessionId;

        return Promise.reject(err);
      },
    );
  }

  /**
   * Get retry condition
   * @returns {boolean} if the retry has to be played or not
   */
  getRetryCondition(err) { // eslint-disable-line
    // 1xx - Do not retry (Informational, request still processing)
    // 2xx - Do not retry (Success)
    // 3xx - Do not retry (Redirect)
    // 4xx - Do not retry (Client errors)
    // 408 - Retry ("Request Timeout")
    // 429 - Retry ("Too Many Requests")
    // 5xx - Retry (Server errors)
    const retryHttpCodes = [408, 429, 500];

    if (err && err.response && retryHttpCodes.indexOf(err.response.status) !== -1) {
      return true;
    }

    return false;
  }

  /**
   * Generate the captcha
   * @return {AxiosPromise<any>}
   */
  createCaptcha() {
    return this.axios.post('/captchas', null, { 'axios-retry': { retries: 0 } });
  }

  /**
   * Get service access
   * @param qs
   * @param device
   * @return {AxiosPromise<any>}
   */
  postAccess({ qs = {}, device }) {
    const {
      service, origin, origine, return_url, webViewMode, login, ctoken, create, // eslint-disable-line
    } = qs;

    const data = {
      service,
      origin,
      origine,
      return_url,
      webViewMode,
      login,
      ctoken,
      create,
      deviceType: device || undefined,
    };
    return this.axios.post('/access', data);
  }

  /**
   * Delete the current session, all still open cookies and session about partners api.
   * @return {AxiosPromise}
   */
  removeAccess() {
    return this.axios.delete('/access/me');
  }

  /**
   * Create an identities identifier
   * @param login
   * @param captcha
   * @return {AxiosPromise<any>}
   */
  createIdentities({ login, captcha }) {
    return this.axios.post('/identities', { login, captcha });
  }

  /**
   * Check login syntax
   * @param login
   * @return {AxiosPromise<any>}
   */
  checkCredentials({ login }) {
    return this.simpleAxios.post('/credentials', { login });
  }

  /**
   * Get all non sensitive data of the current identity
   * @param identitiesId
   * @return {AxiosPromise<any>}
   */
  getIdentities({ identitiesId }) {
    return this.axios.get(`/identities/${identitiesId}`);
  }

  /**
   * Get the list of the non obfuscated contacts of the current identity
   * @param identitiesId
   * @return {AxiosPromise<any>}
   */
  getIdentitiesContacts({ identitiesId }) {
    return this.axios.get(`/identities/${identitiesId}/contacts`);
  }

  /**
   * Create a new otp code sent by partners to the selected contact way
   * @param contactId
   * @return {AxiosPromise<any>}
   */
  createOtp({ contactId }) {
    return this.axios.post('/otps', { contactId });
  }

  /**
   * Retry sending TOC code
   * @param contactId
   * @return {AxiosPromise<any>}
   */
  retryOtp({ contactId }) {
    return this.axios.post('/otps/retry', { contactId });
  }

  /**
   * Send the otp identifier and otp code to partners to check his validity
   * @param otpsId
   * @param otpCode
   * @return {AxiosPromise<any>}
   */
  checkOtpCode({ otpsId, otpCode }) {
    return this.axios.post(`/otps/${otpsId}`, { otpcode: otpCode }, {
      headers: {
        'X-HTTP-Method-Override': 'PATCH',
      },
    });
  }

  /**
   * Send a letter to the user with it otp code inside
   * @param postalCode
   * @return {AxiosPromise<any>}
   */
  sendLetter({ postalCode }) {
    return this.axios.post('/letter', { postalCode });
  }

  /**
   * Check if a letter can be sent to the user
   * @return {AxiosPromise<any>}
   */
  checkPendingLetter() {
    return this.axios.get('/letter');
  }

  /**
   * Update the new password of the user
   * @param identitiesId
   * @param password
   * @param passAgain
   * @return {AxiosPromise<any>}
   */
  patchPassword({ identitiesId, password, passAgain }) {
    return this.axios.post(`/identities/${identitiesId}`, {
      password,
      passAgain,
    }, {
      headers: {
        'X-HTTP-Method-Override': 'PATCH',
      },
    });
  }

  checkPropagationPartnersPassword({ identitiesId }) {
    return this.axios.get(`/identities/${identitiesId}/status`);
  }

  /**
   * Return all data about one contact way
   * @param identitiesId
   * @param hashId
   * @return {AxiosPromise<any>}
   */
  getContactWayData({ identitiesId, hashId }) {
    return this.axios.get(`/identities/${identitiesId}/contacts/${hashId}`);
  }

  /**
   * Update the contacts modes of the user()
   * @param mail
   * @param mobile
   * @param checked
   * @return {AxiosPromise<any>}
   */
  updateContacts({ mail, mobile, checked }) {
    const data = {
      mail,
      mobile,
      checked,
    };
    return this.axios.post('/contacts', data);
  }

  /**
   * Delete all the contacts ways of the current identities
   * @return {AxiosPromise}
   */
  deleteContacts() {
    return this.axios.delete('/contacts');
  }

  /**
   * Send user data to profile useful information
   * Like component render, request time, device type, resolution etc...
   * @param data
   * @return {AxiosPromise<any>}
   */
  sendProbes(data) {
    return this.simpleAxios.post('/report/probe', data);
  }

  /**
   * Send error to profile useful information
   * Like component render, request time, device type, resolution etc...
   * @param data
   * @return {AxiosPromise<any>}
   */
  sendError(data) {
    return this.simpleAxios.post('/report/error', data);
  }
}

export default new ApiService({
  host: window.reactAppEnv.host,
  retryDelay: window.reactAppEnv.apiRules.retryDelay,
  retryTimeout: window.reactAppEnv.apiRules.retryTimeout,
});
